/* globals jQuery, NEIHR */
/* eslint-disable @wordpress/no-global-active-element */

/**
 * External dependencies
 */
import CliboardJS from 'clipboard';

/**
 * Internal dependencies
 */
import delegateEventListener from './theme/delegate-event';
import determineTarget from './theme/determine-target';
import toggleElement from './theme/toggle-element';
import setupStickyObserver from './theme/sticky-observer';
import setupTabs from './theme/tabs';
import setupAccordions from './theme/accordions';
import setupCarousels from './theme/carousels';
import setupModals from './theme/modals';
import setupDropdowns from './theme/dropdown';
import setupShareButtons from './theme/share-button';
import setupDirectoryModules from './theme/ajax-directory';
import setupHeadingObserver from './theme/heading-observer';
import debounce from './theme/debounce';

jQuery( $ => {
	// =========================
	// ! Flag Interaction Method
	// =========================

	function flagMouseUse() {
		document.body.classList.add( 'using-mouse' );
		document.body.classList.remove( 'using-keyboard' );
	}
	document.addEventListener( 'mousemove', flagMouseUse );
	document.addEventListener( 'touchstart', flagMouseUse );
	document.addEventListener( 'keydown', event => {
		if ( event.key === 'Tab' ) {
			document.body.classList.remove( 'using-mouse' );
			document.body.classList.add( 'using-keyboard' );
		}
	} );

	// =========================
	// ! External Link Mandate
	// =========================

	document.querySelectorAll( 'a[href^="http"]' ).forEach( anchor => {
		if ( ! anchor.target && ! anchor.href.startsWith( window.location.origin ) ) {
			anchor.setAttribute( 'target', '_blank' );
			anchor.setAttribute( 'rel', 'noreferrer noopener' );
		}
	} );

	// =========================
	// ! Basic Toggles
	// =========================

	delegateEventListener( 'click', '.js-toggle', ( event, trigger ) => {
		const target = determineTarget( trigger );
		if ( target ) {
			event.preventDefault();
			const isOpen = toggleElement( target, null, 'focus' );

			// For global styling that might be needed
			document.body.classList.toggle( `show-${ target.id }`, isOpen );
		}
	} );

	delegateEventListener( 'click', '.js-close', ( event, trigger ) => {
		event.preventDefault();
		toggleElement( trigger.closest( '[aria-hidden="false"]' ), false );
	} );

	document.addEventListener( 'click', event => {
		document.querySelectorAll( '.js-click-outside-to-close' ).forEach( element => {
			if (
				! element.contains( event.target ) && determineTarget( event.target ) !== element &&
				! event.target.classList.contains( 'js-carousel-pagination-button' ) // ignore carousel stuff
			) {
				toggleElement( element.querySelector( '[aria-hidden="false"]' ) ?? element, false );
			}
		} );
	} );

	// =========================
	// ! Intersection Observers
	// =========================

	if ( typeof IntersectionObserver !== 'undefined' ) {
		setupStickyObserver();

		setupHeadingObserver( 'h3.wp-block-heading[id]', '.neihr-article' );

		// =========================
		// ! In-View Tracking
		// =========================

		const inviewTargets = new WeakMap();
		const inviewObserver = new IntersectionObserver( changes => {
			changes.forEach( change => {
				if ( ! inviewTargets.has( change.target ) ) {
					inviewTargets.set( change.target, true );
					if ( change.isIntersecting || change.intersectionRatio > 0 || change.boundingClientRect.top < 0 ) {
						$( change.target ).removeClass( 'lazy' ).parents( '.lazy' ).removeClass( 'lazy' );
					}
				} else if ( change.isIntersecting || change.intersectionRatio > 0 ) {
					$( change.target ).addClass( 'in-view' ).parents( '.lazy' ).addClass( 'in-view' );
					$( change.target ).prevAll( '.lazy' ).addClass( 'in-view' ).children( '.lazy' ).addClass( 'in-view' );
				}
			} );
		}, {
			rootMargin: '-25% 0px 0px 0px',
		} );

		$( '.page-content, .wp-block-group[id], .neihr-wrapper, .lazy-load-content' ).children().addClass( 'lazy' ).each( function() {
			inviewObserver.observe( this );
		} );
	}

	// =========================
	// ! Item/Image Rotation
	// =========================

	$( '.js-item-rotation' ).each( function() {
		let enabled = true;
		const $items = $( this ).children();
		const $count = $items.length;
		const delay = 3000;
		let currentItem = 0;
		$items.first().addClass( 'is-current' );

		const mediaQuery = window.matchMedia( '(prefers-reduced-motion: reduce)' );
		function checkReducedMotion() {
			enabled = ! mediaQuery.matches;
		}

		checkReducedMotion();
		if ( typeof mediaQuery.addEventListener === 'undefined' ) {
			mediaQuery.addListener( () => checkReducedMotion );
		} else {
			mediaQuery.addEventListener( 'change', checkReducedMotion );
		}

		function setItem( index ) {
			$items.removeClass( 'is-current' );
			$items.eq( index ).addClass( 'is-current' );
		}
		function slide() {
			if ( ! enabled ) {
				return;
			}

			currentItem = currentItem < $count - 1 ? currentItem + 1 : 0;
			setItem( currentItem );
			setTimeout( slide, delay );
		}

		if ( enabled ) {
			setTimeout( slide, delay );
		}
	} );

	// =========================
	// ! Modals
	// =========================

	// Add classes to modals automatically
	$( '.neihr-modal' ).addClass( 'js-modal' );
	$( '.neihr-modal__close' ).addClass( 'js-modal-close' );
	$( '.neihr-modal__frame' ).addClass( 'js-modal-backdrop' );

	setupModals( NEIHR.endpoints || [] );

	// =========================
	// ! Gallery Carousels
	// =========================

	// Prep carousel galleries for functionality
	$( '.neihr-carousel' ).each( function() {
		$( this ).addClass( 'js-carousel' );
		$( this ).find( '.neihr-carousel__slides' ).addClass( 'js-carousel-track' )
			.children().addClass( 'js-carousel-slide' );
		$( this ).find( '.neihr-carousel__captions' ).addClass( 'js-carousel-caption' );
		$( this ).find( '.neihr-carousel-slide figcaption' ).addClass( 'js-slide-caption' );
	} );

	$( '.neihr-showcase' ).each( function() {
		$( this ).find( '.neihr-showcase__body' ).addClass( 'js-carousel' );
		$( this ).find( '.neihr-showcase__slides' ).addClass( 'js-carousel-track' )
			.children().addClass( 'js-carousel-slide' );
		$( this ).find( '.neihr-showcase__captions' ).addClass( 'js-carousel-caption' );
		$( this ).find( '.neihr-showcase-slide figcaption' ).addClass( 'js-slide-caption' );
	} );

	setupCarousels( { animate: true } );

	// Showcase
	const columnsBreakpoint = window.matchMedia( `screen and (min-width: ${ 600 / 16 }em)` );

	$( '.neihr-showcase' ).each( function() {
		const timer = 6000;

		const $showcase = $( this );
		const $toggles = $showcase.find( '.neihr-showcase__toggle' );
		const $panels = $showcase.find( '.neihr-showcase__body' );
		const $pagination = $showcase.find( '.js-carousel-pagination' );
		let $paginationButtons = $showcase.find( '.js-carousel-pagination-button' );

		// Copy caption content to pagination button
		$panels.each( function() {
			const $captions = $( this ).find( '.js-slide-caption' );
			$paginationButtons = $( this ).find( '.js-carousel-pagination-button' );

			$( this ).attr( 'aria-hidden', true );

			$paginationButtons.wrap( "<div class='neihr-showcase__pagination-button-wrapper is-layout-flow'></div>" );

			const count = $captions.length;

			// copy caption content to pagination button wrapper
			for ( let i = 0; i < count; i++ ) {
				const $painationButtonWrapper = $( this ).find( '.neihr-showcase__pagination-button-wrapper' );
				const title = $captions.eq( i ).find( '.neihr-showcase-slide__title' ).text();
				const kicker = $captions.eq( i ).find( '.is-style-kicker ' ).clone();
				const text = $captions.eq( i ).find( '.neihr-showcase-slide__text' ).clone();
				const button = $captions.eq( i ).find( '.wp-block-button' ).clone();
				const date = $captions.eq( i ).find( '.neihr-showcase-slide__date' ).clone();
				$painationButtonWrapper.eq( i ).prepend( date, kicker );
				$paginationButtons.eq( i ).text( title );
				$painationButtonWrapper.eq( i ).append( text, button );
			}

			$captions.remove();
		} );

		$toggles.attr( {
			role: 'tab',
			'aria-selected': false,
			tabindex: '-1',
		} );
		$panels.attr( {
			role: 'tabpanel',
			'aria-hidden': true,
		} );

		// Activate first tab
		const $firstTab = $toggles.first();
		const firstTarget = determineTarget( $firstTab[ 0 ] );

		$firstTab.attr( {
			'aria-selected': true,
			tabindex: '0',
		} );

		if ( firstTarget ) {
			$( firstTarget ).attr( 'aria-hidden', false );
		}

		let current = 0;
		let timeout;
		let isAutoAdvancing = true;
		let ignoreClick = false;
		function autoAdvance() {
			current++;

			const $panel = $panels.filter( '[aria-hidden="false"]' );
			const $panelScroll = $panel.find( '.js-carousel-pagination' );
			const $buttons = $panel.find( '.js-carousel-pagination-button' );

			if ( current >= $buttons.length ) {
				current = 0;
			}

			if ( columnsBreakpoint.matches ) {
				ignoreClick = true;
				$buttons.eq( current ).trigger( 'click' );
				ignoreClick = false;

				$panelScroll[ 0 ].scrollTo( {
					top: $buttons[ current ].parentElement.offsetTop,
				} );
			}

			timeout = setTimeout( autoAdvance, timer );
		}
		timeout = setTimeout( autoAdvance, timer );

		$( this ).on( 'click', '.js-carousel-pagination-button', () => {
			if ( ! ignoreClick ) {
				isAutoAdvancing = false;
				clearTimeout( timeout );
			}
		} );

		// Handle toggle click
		$toggles.on( 'click', function() {
			const target = determineTarget( this );

			if ( ! target ) {
				return;
			}

			const $activeTab = $( this );
			const $targetPanel = $( target );

			// Update tabs
			$toggles.attr( {
				'aria-selected': false,
				tabindex: '-1',
			} );

			$activeTab.attr( {
				'aria-selected': true,
				tabindex: '0',
			} ).focus();

			// Update panels
			$panels.attr( 'aria-hidden', true );
			$targetPanel.attr( 'aria-hidden', false );

			// Scroll active pagination button into view
			const $current = $targetPanel.find( '.js-carousel-pagination-button[aria-selected="true"]' ).parent();
			current = Math.max( 0, $current.index() );
			if ( $current.length > 0 ) {
				const top = $current.offset().top - $pagination.offset().top + $pagination.scrollTop();
				$pagination.scrollTop( top );
			}
			if ( isAutoAdvancing ) {
				clearTimeout( autoAdvance );
				timeout = setTimeout( autoAdvance, timer );
			}
		} );
	} );

	// =========================
	// ! Tab Set
	// =========================

	setupTabs();

	// =========================
	// ! Accordion
	// =========================

	// Add classes to accordion blocks automatically
	$( '.neihr-accordion__toggle' ).addClass( 'js-accordion-toggle' );
	$( '.neihr-accordion__content' ).addClass( 'js-accordion-content' );

	// Auto open conditions: contains search highlight, or is target
	$( '.neihr-accordion:has(mark) .neihr-accordion__toggle' ).addClass( 'auto-open' );
	if ( document.location.hash.length > 1 ) {
		$( `.neihr-accordion${ document.location.hash } .neihr-accordion__toggle` ).addClass( 'auto-open' );
	}

	setupAccordions( NEIHR.endpoints || [] );

	// =========================
	// ! Scroller Buttons
	// =========================

	function makeScrollerButton( type ) {
		const button = document.createElement( 'div' );
		button.className = `neihr-scroller__button ${ type }`;
		button.setAttribute( 'aria-hidden', true );
		return button;
	}

	const scrollerGaps = new WeakMap();
	const scrollerGapObserver = new ResizeObserver( entries => {
		entries.forEach( entry => {
			const gap = parseInt( getComputedStyle( entry.target ).gap.replace( 'px', '' ) );
			scrollerGaps.set( entry.target, gap );
		} );
	} );

	document.querySelectorAll( '.neihr-scroller' ).forEach( scroller => {
		const header = scroller.querySelector( '.neihr-scroller__header' );
		const inner = scroller.querySelector( '.neihr-scroller__inner' );

		const nav = document.createElement( 'div' );
		nav.classList.add( 'neihr-scroller__nav' );
		header.append( nav );

		const buttonsBlock = header.querySelector( '.wp-block-buttons:last-child' );
		if ( buttonsBlock ) {
			nav.append( buttonsBlock );
		}

		const nextButton = makeScrollerButton( 'next' );
		const prevButton = makeScrollerButton( 'prev' );
		const buttons = [ nextButton, prevButton ];

		nav.append( prevButton, nextButton );

		prevButton.hidden = true;
		inner.addEventListener( 'scroll', () => {
			nextButton.hidden = inner.scrollLeft + inner.offsetWidth >= inner.scrollWidth;
			prevButton.hidden = inner.scrollLeft === 0;
		} );

		scrollerGapObserver.observe( inner );

		const sampleItem = inner.firstElementChild;
		buttons.forEach( button => {
			button.addEventListener( 'click', () => {
				if ( button.hidden ) {
					return;
				}

				const gap = scrollerGaps.get( inner ) ?? 0;
				if ( button.classList.contains( 'next' ) ) {
					inner.scrollBy( {
						left: sampleItem.offsetWidth + gap,
					} );
				} else {
					inner.scrollBy( {
						left: -1 * ( sampleItem.offsetWidth + gap ),
					} );
				}
			} );
		} );
	} );

	document.addEventListener( 'keyup', event => {
		if ( event.key === 'Tab' ) {
			const scroller = document.activeElement?.closest( '.neihr-scroller__inner' );
			if ( scroller ) {
				document.activeElement.scrollIntoView( {
					block: 'nearest',
					inline: 'nearest',
				} );
			}
		}
	} );

	// =========================
	// ! Dropdown
	// =========================

	$( '.gfield:has(.ginput_container_select)' ).addClass( 'js-dropdown' );

	setupDropdowns();

	// =========================
	// ! Excerpt Handling
	// =========================

	$( document ).on( 'click', '.js-expand', function() {
		const expanded = $( this ).closest( '.is-expandable' ).toggleClass( 'is-expanded' ).hasClass( 'is-expanded' );

		// Swap the text with the cached label
		const label = $( this ).data( 'label' ) || ( expanded ? 'Show less' : 'Show more' );
		$( this ).data( 'label', $( this ).text() );
		$( this ).text( label );
	} );

	// =========================
	// ! Hidden Target Handling
	// =========================

	function handleHiddenTarget( event ) {
		// Get the target element
		const target = document.getElementById( window.location.hash.substring( 1 ) );
		if ( ! target ) {
			return;
		}

		// See if it's within a hidden container
		const container = target.closest( '[id][aria-hidden="true"]' );
		if ( ! container ) {
			return;
		}

		// Find the control for the container
		const control = document.querySelector( `[aria-controls="${ container.id }"], [data-target="${ container.id }"]` );
		if ( ! control ) {
			return;
		}

		// Prevent automatic scolling
		if ( event ) {
			event.preventDefault();
		}

		// Open the container and focus
		control.click();
		target.scrollIntoView();
	}

	window.addEventListener( 'hashchange', handleHiddenTarget );
	handleHiddenTarget();

	// =========================
	// ! Bookmark/Share Links
	// =========================

	setupShareButtons();

	$( 'a.js-clipboard' ).on( 'click', e => e.preventDefault() );

	const hrefClipboard = new CliboardJS( 'a.js-clipboard', {
		text( trigger ) {
			return trigger.getAttribute( 'href' );
		},
	} );

	const clipboardTooltipTimeouts = new WeakMap();
	hrefClipboard.on( 'success', e => {
		clearTimeout( clipboardTooltipTimeouts.get( e.trigger ) );
		$( e.trigger ).addClass( 'js-copied' ).attr( 'aria-label', 'Link copied!' );
		const timeout = setTimeout( () => $( e.trigger ).removeClass( 'js-copied' ), 5000 );
		clipboardTooltipTimeouts.set( e.trigger, timeout );
	} );

	// =========================
	// ! Stat Counters
	// =========================

	const counterAnimations = new WeakMap();
	function animateCount( element, newNumber ) {
		if ( ! element ) {
			return;
		}

		const current = parseInt( element.innerText || '0' );
		if ( current === newNumber ) {
			return;
		}

		const previous = counterAnimations.get( element );
		if ( previous ) {
			previous.stop = true;
		}

		const delta = current > newNumber ? -1 : 1;
		const diff = Math.abs( current - newNumber );
		const start = performance.now();

		function step() {
			if ( step.stop || current === newNumber ) {
				return;
			}

			const elapsed = performance.now() - start;
			const progress = Math.min( elapsed / 500, 1 );

			element.innerText = Math.round( current + ( diff * progress * delta ) );

			if ( progress < 1 ) {
				requestAnimationFrame( step );
			}
		}
		requestAnimationFrame( step );
	}

	document.querySelectorAll( '.neihr-stat__value' ).forEach( element => {
		const value = parseInt( element.innerText );
		if ( ! isNaN( value ) && value > 1 ) {
			element.classList.add( 'js-counter' );
			element.dataset.value = value;
			element.innerText = '';
		}
	} );

	document.querySelectorAll( '.js-counter' ).forEach( counter => {
		animateCount( counter, counter.dataset.value );
	} );

	// =========================
	// ! Filterable List Handling
	// =========================

	document.querySelectorAll( '.js-filterable' ).forEach( container => {
		const searchInput = container.querySelector( '.js-filterable-search' );
		const items = container.querySelectorAll( '.js-filterable-item' );

		function onSearch() {
			const query = searchInput.value.normalize( 'NFD' ).replace( /[\u0300-\u036f]/g, '' ).toLowerCase();

			items.forEach( item => {
				const value = item.dataset?.value ?? item.innerText ?? '';
				if ( query && ! value.includes( query ) ) {
					item.hidden = true;
				} else {
					item.hidden = false;
				}
			} );
		}

		searchInput?.addEventListener( 'input', debounce( onSearch, 500 ) );
	} );

	// =========================
	// ! Directory Module
	// =========================

	setupDirectoryModules();
} );
